import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '../lib/api.ts'
import { BUTTON, ErrorNote, INPUT, IssuedLink, LABEL, LINK_BUTTON, linkFor, useIssueState } from '../lib/admin.tsx'
import { selectCampaign, useSelectedCampaign } from '../lib/campaign.tsx'
import { invitableRoles, roleLabel } from '../lib/roles.ts'
import { useSession } from '../lib/session.ts'

/**
 * A campaign's team (2026-09-30): everyone on it and their role HERE. Assigning
 * people happens from this page — the People page is the reverse lookup.
 *
 * campaign.list_team decides which rows the caller sees (a Field lieutenant
 * sees their canvassers, not the consultant), and every action refuses in the
 * database on its own: removing a role needs standing to have granted it
 * (0086), a reset needs standing over the whole person (0040).
 */

interface Member {
  grant_id: string
  user_id: string
  role: string
  granted_at: string
  email: string | null
  username: string | null
  display_name: string | null
  user_status: string
  may_administer: boolean
  also_on: { campaign_id: string; name: string; role: string }[]
}

/** The workspace page: the team of the campaign chosen in the header. */
export function Team() {
  const { campaign, campaignId } = useSelectedCampaign()
  if (!campaign) return <p className="text-sm text-ink-muted">Loading…</p>
  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight">Team</h1>
      <p className="mt-1 text-sm text-ink-muted">
        Everyone on {campaign.name} and their role here.
      </p>
      <TeamPanel campaignId={campaignId} roleHere={campaign.role} />
    </div>
  )
}

/**
 * The team table and invite form, shared by the workspace Team page and the
 * Platform campaign page. `roleHere` only decides what to offer.
 */
export function TeamPanel({ campaignId, roleHere }: { campaignId: string; roleHere: string }) {
  const client = useQueryClient()
  const { data: session } = useSession()
  const myId = session?.state === 'authenticated' ? session.user_id : null
  const { issued, setIssued, error, setError, report } = useIssueState()
  const offer = invitableRoles(roleHere)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState(offer[0] ?? '')

  const team = useQuery({
    queryKey: ['team', campaignId],
    queryFn: () => apiGet<{ team: Member[] }>(`/campaigns/${campaignId}/team`),
    enabled: Boolean(campaignId),
  })
  const refresh = () => client.invalidateQueries({ queryKey: ['team', campaignId] })

  const invite = useMutation({
    mutationFn: () =>
      apiPost<{ token: string }>('/admin/invitations', {
        email: email.trim(),
        role,
        scope_type: 'campaign',
        scope_id: campaignId,
      }),
    onSuccess: (r) => {
      setError(null)
      setIssued({
        label: `Invitation for ${email.trim()} as ${roleLabel(role)}`,
        url: linkFor('/accept', r.token),
      })
      setEmail('')
    },
    onError: report,
  })

  const remove = useMutation({
    mutationFn: (m: Member) => apiPost(`/admin/grants/${m.grant_id}/revoke`),
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: report,
  })

  const resetPassword = useMutation({
    mutationFn: (m: Member) =>
      apiPost<{ token: string }>(`/admin/users/${m.user_id}/password-reset`).then((r) => ({ r, m })),
    onSuccess: ({ r, m }) => {
      setError(null)
      setIssued({ label: `Password reset for ${m.email}`, url: linkFor('/reset', r.token) })
    },
    onError: report,
  })

  const resetMfa = useMutation({
    mutationFn: (m: Member) => apiPost(`/admin/users/${m.user_id}/mfa-reset`),
    onSuccess: () => setError(null),
    onError: report,
  })

  const members = team.data?.team ?? []

  return (
    <div>
      <ErrorNote error={error} />
      <IssuedLink issued={issued} onDismiss={() => setIssued(null)} />

      {offer.length > 0 ? (
        <section className="mt-6 rounded-lg border border-line bg-surface-sunken p-4">
          <h2 className="text-sm font-semibold">Add someone to this campaign</h2>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className={LABEL}>Email</span>
              <input
                id="team-invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className={`w-64 ${INPUT}`}
              />
            </label>
            <label className="text-sm">
              <span className={LABEL}>Role here</span>
              <select
                id="team-invite-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className={INPUT}
              >
                {offer.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={!email.trim() || !role || invite.isPending}
              onClick={() => invite.mutate()}
              className={BUTTON}
            >
              {invite.isPending ? 'Creating…' : 'Create invitation'}
            </button>
          </div>
          <p className="mt-3 text-xs text-ink-muted">
            Someone who already has an account gets the same link: they sign in and accept, and
            their password is never touched by it. Canvassers are set up on the{' '}
            <Link to="/canvassers" onClick={() => selectCampaign(campaignId)} className="text-brand-700 hover:underline">
              Canvassers
            </Link>{' '}
            page.
          </p>
        </section>
      ) : null}

      <div className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[44rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs tracking-wide text-ink-muted uppercase">
              <th scope="col" className="py-2 pr-4 font-semibold">Person</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Role here</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Account</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Also on</th>
              <th scope="col" className="relative py-2 font-semibold"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {team.isPending ? (
              <tr><td colSpan={5} className="py-4 text-ink-muted">Loading…</td></tr>
            ) : members.length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-ink-muted">Nobody here yet that you can see.</td></tr>
            ) : (
              members.map((m) => {
                const web = m.email !== null
                // Not on your own row: resetting your own second factor or
                // removing yourself from here is a way to lock yourself out,
                // and the Security page is where you change your own account.
                const self = m.user_id === myId
                const canRemove = web && !self && offer.includes(m.role)
                return (
                  <tr key={m.grant_id} className="border-b border-line/60 align-top">
                    <td className="py-2 pr-4">
                      <div className="font-medium">{m.display_name ?? m.email ?? m.username}</div>
                      <div className="text-xs text-ink-muted">{m.email ?? m.username}</div>
                    </td>
                    <td className="py-2 pr-4">{roleLabel(m.role)}</td>
                    <td className="py-2 pr-4">
                      <span className="whitespace-nowrap rounded-full border border-line px-2 py-0.5 text-xs text-ink-muted">
                        {web ? 'Web · 2FA' : 'Walk app only'}
                      </span>
                      {m.user_status !== 'active' ? (
                        <div className="mt-0.5 text-xs text-ink-muted">{m.user_status}</div>
                      ) : null}
                    </td>
                    <td className="py-2 pr-4 text-xs text-ink-muted">
                      {m.also_on.length === 0
                        ? '—'
                        : m.also_on
                            .map((o) => (o.role === m.role ? o.name : `${o.name} (${roleLabel(o.role)})`))
                            .join(', ')}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {!web ? (
                        <Link to="/canvassers" onClick={() => selectCampaign(campaignId)} className={LINK_BUTTON}>
                          Canvassers page
                        </Link>
                      ) : null}
                      {self ? <span className="text-xs text-ink-muted">you</span> : null}
                      {web && !self && m.may_administer ? (
                        <>
                          <button type="button" onClick={() => resetPassword.mutate(m)} className={`mr-3 ${LINK_BUTTON}`}>
                            Reset password
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                confirm(
                                  `Remove every second factor for ${m.email}? They will be signed out ` +
                                    `everywhere and must enrol again before they can log in.\n\n` +
                                    `Confirm who you are speaking to first — this is the step an ` +
                                    `attacker would ask you to take.`,
                                )
                              ) {
                                resetMfa.mutate(m)
                              }
                            }}
                            className={`mr-3 ${LINK_BUTTON}`}
                          >
                            Reset 2FA
                          </button>
                        </>
                      ) : null}
                      {canRemove ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`Remove ${m.email} from this campaign? Their other campaigns are not affected.`)) {
                              remove.mutate(m)
                            }
                          }}
                          className="text-xs text-ink-muted hover:text-ink"
                        >
                          Remove
                        </button>
                      ) : null}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
